import React, { useState, useMemo } from 'react';
import { Route } from 'react-router-dom';
import {
  CSSTransition,
  Transition,
  TransitionGroup,
} from 'react-transition-group';
import MediaQuery from 'react-responsive';
import './styles.css';
import {handleEnterAnimation, handleExitAnimation} from '../../animations/timelines/modal-transitions';
import FiltersContentLoader from '../../components/FiltersContentLoader';
import GridHistoryWithFilters from '../../components/GridHistoryWithFilters';
import HorizontalLoader from '../../components/HorizontalLoader';
import ListSearch from '../../components/ListSearch';
import ListSearchContentLoader from '../../components/ListSearchContentLoader';
import NoEventsMask from '../../components/NoEventsMask';
import {ANIMATION_TIMEOUT_APPEAR, ANIMATION_TIMEOUT_INOUT} from '../../constants/animation';
import ENDPOINT from '../../constants/endpoints';
import {updateHistory} from '../../helpers/grid-history';
import {useGrid} from '../../hooks/useGrid';
import {useGridFilters} from '../../hooks/useGridFilters';
import {useReviewAction} from '../../hooks/useReviewAction';
import PageLayout from '../../layouts/PageLayout';
import FiltersAccordion from '../../scenes/FiltersAccordion';
import FiltersWidget from '../../scenes/FiltersAccordion/components/FiltersWidget';
import List from '../../scenes/List';
import ReviewItem from '../../scenes/List/components/ReviewItem';
import Sidebar from '../../scenes/Sidebar';
import ActionButtonsWidget from '../../scenes/Sidebar/components/ActionButtonsWidget';
import FiltersMobileToggler from '../../scenes/Sidebar/components/FiltersMobileToggler';
import WidgetOnMobile from '../../scenes/Sidebar/components/WidgetOnMobile';

const Reviews = props => {
  const { user, history, globalOptions } = props;

  const {
    rate: rateReview,
    view: handleClickReviewLink,
    message: handleClickSendMessage,
    isAllowedRate: handleBeforeReviewRatingUpdate,
  } = useReviewAction(user, globalOptions);

  const filters = useGridFilters(ENDPOINT.FILTERS.REVIEWS);

  const grid = useGrid(ENDPOINT.COLLECTION.REVIEWS, params => {
    updateHistory(history, params);
  });

  const [mobileFilters, toggleMobileFilters] = useState(false);

  const toggleMobileFiltersHandler = () => {
    toggleMobileFilters(!mobileFilters);
  };

  const filtersAccordion = useMemo(
    () =>
      filters.data && (
        <FiltersAccordion
          title={'Search Review'}
          subTitle={
            'Use the search filters below to narrow your review search.'
          }
          filters={filters.data}
          onChange={filters.actions.change}
          onGroupSelectAll={filters.actions.groupSelect}
          onGroupClearAll={filters.actions.groupClear}
          onClearAll={filters.actions.clear}
          onSearch={filters.actions.search}
          onPushValue={filters.actions.push}
          render={renderProps => <FiltersWidget {...renderProps} />}
        />
      ),
    [filters.data]
  );

  const sortControls = [
    {
      defaultCaption: 'Most Recent',
      toggledCaption: 'Most Recent',
      value: 'recent',
    },
    {
      defaultCaption: 'Most Useful',
      toggledCaption: 'Most Useful',
      value: 'useful',
    },
  ];

  const handleReviewRatingUpdate = (review, value) => {
    rateReview(review, value).then(response =>
      grid.actions.setDataRows(
        grid.data.rows.map(row =>
          row.ReviewID === response.ReviewID ? { ...row, ...response } : row
        )
      )
    );
  };

  return (
    <GridHistoryWithFilters
      history={history}
      grid={grid}
      filters={filters}
      initialParams={{ sortBy: 'recent' }}
    >
      <PageLayout
        pageClass="reviews-page"
        pageTitle="Reviews"
        mainContent={
          <>
            <Route
              render={({ location }) => (
                <TransitionGroup component={null}>
                  <Transition
                    key={location.pathname}
                    timeout={500}
                    onEnter={node => handleEnterAnimation(node)}
                    onExit={node => handleExitAnimation(node)}
                  >
                    <>
                      {grid.data.init ? (
                        <ListSearch
                          searchPlaceholder="Search reviews"
                          search={grid.data.params.search || false}
                          sortBy={grid.data.params.sortBy || 'recent'}
                          recordsCount={grid.data.firmsCount}
                          recordsName={
                            grid.data.firmsCount === 1 ? 'firm' : 'firms'
                          }
                          sortControls={sortControls}
                          onChangeSortByHandler={grid.actions.sortBy}
                          onSendSearchQueryHandler={grid.actions.search}
                          renderHeader={
                            <MediaQuery query="(max-width: 767px)">
                              <FiltersMobileToggler
                                title="Search Filters:"
                                toggleFunc={toggleMobileFiltersHandler}
                              />
                            </MediaQuery>
                          }
                        />
                      ) : (
                        <ListSearchContentLoader />
                      )}

                      <div className="horizontal-loader__container">
                        {grid.data.isFetching && <HorizontalLoader />}
                      </div>

                      <div className="reviews-page__reviews-list">
                        <List
                          keyField={'ReviewID'}
                          items={grid.data.rows}
                          itemRender={(item, actions) => (
                            <CSSTransition
                              classNames="item"
                              timeout={ANIMATION_TIMEOUT_INOUT}
                              key={`${item.ReviewType}_${item.ReviewID}`}
                            >
                              <ReviewItem item={item} actions={actions} />
                            </CSSTransition>
                          )}
                          actions={{
                            handleClickSendMessage,
                            handleReviewRatingUpdate,
                            handleClickReviewLink,
                            handleBeforeReviewRatingUpdate,
                            onChangePage: grid.actions.readPage,
                          }}
                          pagination={{
                            page: grid.data.page,
                            count: grid.data.rowsCount,
                            perPage: grid.data.perPage,
                          }}
                        />
                      </div>
                    </>
                  </Transition>
                </TransitionGroup>
              )}
            />
          </>
        }
        sidebarContent={
          grid.data.init ? (
            <Sidebar>
              {grid.data.isFetching && (
                <CSSTransition
                  in={grid.data.init}
                  appear={true}
                  timeout={ANIMATION_TIMEOUT_APPEAR}
                  classNames="fade"
                >
                  <NoEventsMask relative={true} />
                </CSSTransition>
              )}
              <ActionButtonsWidget user={user} history={history} />
              <WidgetOnMobile
                renderComponent={filtersAccordion}
                status={mobileFilters}
                toggleFunc={toggleMobileFiltersHandler}
              />
            </Sidebar>
          ) : (
            <FiltersContentLoader />
          )
        }
      />
    </GridHistoryWithFilters>
  );
};

export default Reviews;
